import React, { useContext } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import classNames from 'classnames';
import { ContentType, FlyoutSide, LessonMode } from '../../Constants';
import lessonService from '../services/LessonService';
import utilsService from '../services/UtilsService';
import ActivityNavigator from '../components/activityViewer/ActivityNavigator';
import ActivityViewer from '../components/activityViewer/ActivityViewer';
import Flyout from '../components/tabbedFlyout/Flyout';
import FullpageLoader from '../components/FullpageLoader';
import InteractiveViewer from '../components/activityViewer/InteractiveViewer';
import LeftRail from '../components/leftRail/LeftRail';
import MultipartActivity from '../components/lessonItems/MultipartActivity';
import QuestionBanner from '../components/QuestionBanner';
import QuestionFlyout from '../components/tabbedFlyout/QuestionFlyout';
import SectionBanner from '../components/SectionBanner';
import StandAloneViewer from '../components/StandAloneViewer';
import BookActivityQuestionFlyout from '../components/tabbedFlyout/BookActivityQuestionFlyout';
import '../../css/ContentLayout.scss';


// TODO remove manuscript class name when all runtime items are created
const ContentLayout = observer(({ isScoring, isScoreModeVideoActivityParent } = {}) => {
  const {
    lessonManager,
    alignmentManager,
    navMenuManager,
    scoringManager
  } = useContext(MobXProviderContext);

  const lessonElementId = lessonManager.currentLessonElementId;

  const mode = lessonManager.playerMode;

  if (!lessonManager ||
    lessonManager.currentSlide < 0 ||
    !lessonManager.getSlideModel(lessonElementId) ||
    !lessonManager.isLoaded) {
    return (<><FullpageLoader /></>);
  }

  const gotOpen = navMenuManager.gotoMenuOpen;

  const model = lessonManager.getSlideModel(lessonElementId);
  const { isActivityPart } = model;
  const { isActivity } = model;

  let showActivityFlyout = false;
  const currentLessonItemHasAlignments = alignmentManager.hasAlignments(lessonElementId);
  // eslint-disable-next-line eqeqeq
  const currentLessonItemHasRubric = (scoringManager.rubricMap.has(lessonElementId) && model.type !== ContentType.RUBRIC.type);
  const currentLessonItemHasTeacherNotes = (!utilsService.isNullOrEmpty(model.teacherNotes));

  let isMultipartActivity = false;
  let isInteractiveActivity = false;
  let isBookleteActivity = false;
  const parentLessonElementId = (isActivityPart) ? model.parentActivityElementId : null;
  let parentModel = null;
  if (isActivityPart) {
    parentModel = lessonManager.getSlideModel(parentLessonElementId);
    const activityParentItemHasAlignments = alignmentManager.hasAlignments(parentLessonElementId);
    showActivityFlyout = (mode === LessonMode.PREVIEW ||
        mode === LessonMode.PUBLISHER_PREVIEW ||
         mode === LessonMode.ITEM_PREVIEW) && (((lessonService.showActivityFlyout(activityParentItemHasAlignments)) ||
      (lessonService.showFlyoutTeacherNotes() && !utilsService.isNullOrEmpty(parentModel.teacherNotes))));
    const activityModel = lessonManager.getSlideModel(parentLessonElementId);
    isInteractiveActivity = activityModel.isInteractiveActivity;
    isMultipartActivity = (activityModel.type === ContentType.MULTIPART_ACTIVITY.type);
    isBookleteActivity = (activityModel.type === ContentType.BOOK_ACTIVITY.type);
  }
  lessonManager.setIsInteractiveViewer(isInteractiveActivity);
  const isSplitActivity = (!isInteractiveActivity && !isMultipartActivity);

  const showStandaloneFlyout = lessonService.showStandaloneFlyout(currentLessonItemHasAlignments,
    currentLessonItemHasRubric, currentLessonItemHasTeacherNotes);

  const showQuestionBanner = (lessonManager.lessonOptions.hasItemNumberBanner &&
    !model.isTitlePage &&
    !model.isSummaryPage);

  const isTitle = (model.isTitlePage);
  const isSummary = (model.isSummaryPage);
  const hasVerticalGoto = !!lessonManager.lessonOptions.hasVerticalGotoMenu;
  const hasRelatedResources = lessonManager.shouldShowRelatedTabButton &&
    lessonManager.playerMode !== LessonMode.TEACHER_PRESENTATION;
  const railShell = hasVerticalGoto || hasRelatedResources || !!navMenuManager.leftRailDirections;
  // Only the parent activity's alignments/notes live in the left rail.
  // Standalone items, activity children, and scoring keep alignments/rubric
  // on the right-side flyout.
  const hostedFlyout = (railShell && !isScoring && !isScoreModeVideoActivityParent && showActivityFlyout) ? {
    flyoutSide: FlyoutSide.LEFT,
    isActivity: isActivityPart,
    isScoring,
    lessonElementId: model.parentActivityElementId || model.lessonElementId
  } : null;
  const hasLeftRail = railShell || !!hostedFlyout;
  lessonManager.setIsQuestionListView(false);

  return (
    <>
      {(!isTitle && !isSummary) && <SectionBanner lessonElementId={lessonElementId} />}
      <QuestionBanner lessonElementId={lessonElementId} showQuestionBanner={showQuestionBanner && (!isActivityPart)} />
      <div id='main-content' className={classNames({
        'content-wrapper': true,
        'manuscript': true,
        'go-to-open': gotOpen,
        'has-left-rail': hasLeftRail,
        'has-related-resources': hasRelatedResources,
        'has-vertical-goto': hasVerticalGoto,
        'left-rail-open-satellite': navMenuManager.leftRailSelectedTab === 'satellite' && hasRelatedResources,
        'left-rail-open-goto': navMenuManager.leftRailSelectedTab === 'goto' && hasVerticalGoto && navMenuManager.gotoMenuOpen,
        'left-rail-open-directions': hasLeftRail && navMenuManager.leftRailSelectedTab === 'directions',
        'left-rail-open-flyout': hasLeftRail && navMenuManager.leftRailSelectedTab === 'flyout',
        'activity': isActivityPart,
        'section-banner': (lessonManager.lessonOptions.hasSectionBanner && !!lessonManager.currentSectionTitle && !isTitle && !isSummary),
        'question-banner': (showQuestionBanner && (!isActivityPart))
      })}
      tabIndex='0'>
        <LeftRail hostedFlyout={hostedFlyout} />
        <div className={classNames({ 'content-stack': true, 'activity': isActivityPart, 'BookletActivity': isBookleteActivity })}>
          {(isActivityPart || isScoreModeVideoActivityParent) ? (
            <div className='test-item-wrapper activity'>
              {(((showActivityFlyout && !isScoring)) || isScoreModeVideoActivityParent) && !hostedFlyout && (
                <Flyout
                  flyoutSide={!isScoreModeVideoActivityParent ? FlyoutSide.LEFT : FlyoutSide.RIGHT}
                  isScoreModeVideoActivityParent={isScoreModeVideoActivityParent}
                  isScoring={isScoring}
                  lessonElementId={model.parentActivityElementId || model.lessonElementId} />
              )}
              {(isInteractiveActivity && !isScoreModeVideoActivityParent) && (
                <>
                  {(parentModel.type !== ContentType.BOOK_ACTIVITY.type && parentModel.type !== ContentType.IMAGE_QUESTION.type) && (
                    <QuestionFlyout
                      isActivity={isActivityPart}
                      lessonElementId={lessonElementId}
                      model={model}
                      showQuestionBanner={showQuestionBanner} />
                  )}
                  <InteractiveViewer lessonElementId={lessonElementId} showQuestionBanner={showQuestionBanner} />
                  {(parentModel.type === ContentType.BOOK_ACTIVITY.type || parentModel.type === ContentType.IMAGE_QUESTION.type) && (
                    <BookActivityQuestionFlyout
                      isActivity={isActivityPart}
                      lessonElementId={lessonElementId}
                      model={model}
                      showQuestionBanner={showQuestionBanner} />
                  )}
                </>
              )}
              {(isMultipartActivity && !isScoreModeVideoActivityParent) && (
                <>
                  {/* Moved to QuestionListViewer */}
                  {/* <AudioPlayer lessonElementId={parentLessonElementId} /> */}
                  <MultipartActivity lessonElementId={lessonElementId} showQuestionBanner={showQuestionBanner} />
                </>
              )}
              {isSplitActivity && <ActivityViewer lessonElementId={lessonElementId} showQuestionBanner={showQuestionBanner} />}
              {(showStandaloneFlyout && !isScoreModeVideoActivityParent) && (
                <Flyout
                  flyoutSide={FlyoutSide.RIGHT}
                  isActivity={isActivityPart}
                  isScoring={isScoring}
                  lessonElementId={lessonElementId} />
              )}
              {(isActivityPart) && (
                <ActivityNavigator activityLessonElementId={model.parentActivityElementId}
                  lessonElementId={lessonElementId} />
              )}
            </div>
          )
            : isActivity ?
              <ActivityViewer lessonElementId={lessonElementId} showQuestionBanner={showQuestionBanner} />
              : (
                <>
                  <div className={classNames({
                    'test-item-wrapper': true,
                    'title': isTitle,
                    'summary': isSummary,
                  }, model.type)}>
                    <StandAloneViewer lessonElementId={lessonElementId} showQuestionBanner={isActivityPart} />
                    {(showStandaloneFlyout && !isScoreModeVideoActivityParent) && (
                      <Flyout
                        flyoutSide={FlyoutSide.RIGHT}
                        isActivity={false}
                        isScoring={isScoring}
                        lessonElementId={lessonElementId} />
                    )}
                  </div>
                </>
              )}
        </div>
      </div>
    </>
  );
});

export default ContentLayout;
